

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewDayLineItemModal from 'components/NewDayLineItemModal';
import MDBadge from "components/MDBadge";
import { formatDate } from 'utils/DateTimeConversion';

const DayLineItems = () => {
    const [lineItems, setLineItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('day_line_items');

    useEffect(() => {
        fetchLineItems();
    }, []);

    const fetchLineItems = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/day-line-items/');
            setLineItems(response.data);
        } catch (error) {
            console.error('Error fetching day line items:', error);
            setSnackbar({ open: true, message: 'Error loading Day Line Items', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveLineItem = async (payload) => {
        try {
            await axiosInstance.post('/day-line-items/', payload);
            setSnackbar({ open: true, message: 'Day Line Item created successfully', severity: 'success' });
            fetchLineItems();
        } catch (error) {
            console.error('Error saving Day Line Item:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/day-line-items/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Title",
            accessor: "title",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || row.original.name || '-'}
                </MDTypography>
            )
        },
        { Header: "Type", accessor: "type" },
        {
            Header: "Mandatory",
            accessor: "mandatory",
            Cell: ({ value }) => (
                <MDBadge
                    badgeContent={value ? "Yes" : "No"}
                    color={value ? "success" : "warning"}
                    variant="gradient"
                    size="sm"
                />
            )
        },
        { Header: "Start", accessor: "start", Cell: ({ value }) => formatDate(value, 'datetime') || '-' },
        { Header: "End", accessor: "end", Cell: ({ value }) => formatDate(value, 'datetime') || '-' },
        { Header: "Geo", accessor: "geo_location" },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: lineItems,
    }), [lineItems]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Day Line Items</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Day Line Item
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : lineItems.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Day Line Item" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewDayLineItemModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveLineItem}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default DayLineItems;
